import axios from 'axios'
import tokenManager from './tokenManager'

/**
 * API Service Layer for Note Taking Application
 * 
 * Provides centralized HTTP client configuration, request/response interceptors,
 * and methods for all backend API operations.
 */

/**
 * Comprehensive IndexedDB clearing for logout security
 * Ensures all user data is removed from browser storage on logout
 */
const clearAllIndexedDBData = async () => {
  // List of all possible IndexedDB databases used by the application
  const databases = [
    'CloudNotesCache',  // Cloud mode note caching
    'NotesAppLocal'     // Local mode storage (only cleared in cloud mode logout)
  ]

  const results = []

  for (const dbName of databases) {
    try {
      // Special handling for local mode - don't clear local data if in local mode
      if (dbName === 'NotesAppLocal') {
        const isLocalMode = localStorage.getItem('localModeEnabled') === 'true'
        if (isLocalMode) {
          console.log(`🔍 Skipping ${dbName} - local mode is enabled`)
          results.push({ database: dbName, status: 'skipped', reason: 'local mode enabled' })
          continue
        }
      }

      // First, try to close any existing connections to the database
      await closeExistingConnections(dbName)
      
      // Wait a bit to ensure connections are closed
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // Now attempt to delete the database
      await deleteDatabase(dbName)
      console.log(`✅ Successfully cleared IndexedDB: ${dbName}`)
      results.push({ database: dbName, status: 'success' })
      
    } catch (error) {
      console.error(`❌ Failed to clear IndexedDB ${dbName}:`, error)
      results.push({ database: dbName, status: 'error', error: error.message })
    }
  }

  return results
}

/**
 * Timeout wrapper for IndexedDB clearing to prevent logout hanging
 */
const clearAllIndexedDBDataWithTimeout = async (timeoutMs = 5000) => {
  return new Promise((resolve) => {
    // Set up timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      console.warn(`⏰ IndexedDB clearing timed out after ${timeoutMs}ms, continuing logout...`)
      resolve([{ database: 'timeout', status: 'error', error: 'Operation timed out' }])
    }, timeoutMs)
    
    // Run the actual cleanup
    clearAllIndexedDBData()
      .then((results) => {
        clearTimeout(timeoutId)
        resolve(results)
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        console.error('❌ IndexedDB clearing failed:', error)
        resolve([{ database: 'error', status: 'error', error: error.message }])
      })
  })
}

/**
 * Close existing connections to a database before deletion
 */
const closeExistingConnections = async (dbName) => {
  return new Promise((resolve) => {
    // Try to open the database to get a connection and then close it
    try {
      const openRequest = indexedDB.open(dbName)
      
      openRequest.onerror = () => {
        // Database might not exist, that's fine
        resolve()
      }
      
      openRequest.onsuccess = () => {
        const db = openRequest.result
        if (db) {
          db.close()
          console.log(`🔒 Closed existing connections to ${dbName}`)
        }
        resolve()
      }
      
      openRequest.onblocked = () => {
        console.warn(`⚠️ Database ${dbName} is blocked, forcing close`)
        resolve() // Continue anyway
      }
      
      // Shorter timeout for background cleanup (1 second instead of 2)
      setTimeout(() => {
        console.warn(`⚠️ Timeout closing connections to ${dbName}`)
        resolve()
      }, 1000)
      
    } catch (error) {
      console.warn(`⚠️ Error closing connections to ${dbName}:`, error)
      resolve() // Continue anyway
    }
  })
}

/**
 * Delete a specific database with retry logic
 * Optimized timeouts for background cleanup
 */
const deleteDatabase = (dbName) => {
  return new Promise((resolve, reject) => {
    let attempts = 0
    const maxAttempts = 3

    const attemptDelete = () => {
      attempts++
      const deleteRequest = indexedDB.deleteDatabase(dbName)
      
      deleteRequest.onerror = () => {
        if (attempts < maxAttempts) {
          console.log(`🔄 Retry ${attempts}/${maxAttempts} for ${dbName}`)
          setTimeout(attemptDelete, 300 * attempts) // Faster retry (300ms instead of 500ms)
        } else {
          reject(new Error(`Failed to delete ${dbName} after ${maxAttempts} attempts: ${deleteRequest.error?.message || 'Unknown error'}`))
        }
      }
      
      deleteRequest.onsuccess = () => {
        resolve()
      }
      
      deleteRequest.onblocked = () => {
        if (attempts < maxAttempts) {
          console.warn(`⚠️ Database ${dbName} deletion blocked, retrying...`)
          setTimeout(attemptDelete, 500 * attempts) // Faster retry for blocked state too
        } else {
          // If still blocked after retries, consider it successful since we tried our best
          console.warn(`⚠️ Database ${dbName} deletion remained blocked, but continuing...`)
          resolve()
        }
      }
    }

    attemptDelete()
  })
}

/**
 * Clear cloud cache IndexedDB on logout (LEGACY - replaced by clearAllIndexedDBData)
 * Kept for backward compatibility but now delegates to the comprehensive function
 */
const clearCloudCache = async () => {
  console.log('⚠️ Using legacy clearCloudCache - consider using clearAllIndexedDBData instead')
  const results = await clearAllIndexedDBData()
  
  // Check if any databases failed to clear
  const failures = results.filter(r => r.status === 'error')
  if (failures.length > 0) {
    throw new Error(`Failed to clear some databases: ${failures.map(f => f.database).join(', ')}`)
  }
}

/**
 * Verify that IndexedDB databases have been cleared (for testing/debugging)
 * Returns a report of what databases still exist
 */
const verifyIndexedDBClearing = async () => {
  const databases = ['CloudNotesCache', 'NotesAppLocal']
  const report = []

  for (const dbName of databases) {
    try {
      const exists = await checkDatabaseExists(dbName)
      report.push({
        database: dbName,
        exists: exists,
        status: exists ? 'NOT CLEARED' : 'CLEARED'
      })
    } catch (error) {
      report.push({
        database: dbName,
        exists: 'UNKNOWN',
        status: 'ERROR',
        error: error.message
      })
    }
  }

  console.log('🔍 IndexedDB Clearing Verification Report:', report)
  return report
}

/**
 * Check if a specific IndexedDB database exists
 */
const checkDatabaseExists = (dbName) => {
  return new Promise((resolve) => {
    try {
      const openRequest = indexedDB.open(dbName)
      
      openRequest.onerror = () => {
        // Database doesn't exist or can't be opened
        resolve(false)
      }
      
      openRequest.onsuccess = () => {
        const db = openRequest.result
        const exists = db.objectStoreNames.length > 0
        db.close()
        resolve(exists)
      }
      
      openRequest.onblocked = () => {
        // Database is blocked, assume it exists
        resolve(true)
      }
      
      // Timeout after 2 seconds
      setTimeout(() => {
        resolve(false)
      }, 2000)
      
    } catch (error) {
      resolve(false)
    }
  })
}

// Create axios instance with optimized configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080',
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for adding auth token and optimizing requests
api.interceptors.request.use(
  (config) => {
    // Get fresh JWT token from TokenManager
    const token = tokenManager.getJWTToken()
    if (token) {
      config.headers.Authorization = `Basic ${token}`
      console.log('🔍 API: Auth token: Present')
    } else {
      console.log('🔍 API: Auth token: Missing')
    }
    
    // Optimize timeout for different request types
    if (config.url?.includes('/notes') && config.method === 'get') {
      // For note fetching, use longer timeout for initial sync
      const isInitialSync = !localStorage.getItem('lastSyncTime')
      config.timeout = isInitialSync ? 60000 : 30000 // 60s for initial, 30s for regular
    }
    
    console.log(`🔍 API: Making request to: ${config.url}`)
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for handling responses and implementing retry logic
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      // Check if this is a Google Drive API request
      if (originalRequest.url?.includes('googleapis.com')) {
        console.log('❌ API: Google Drive token expired, attempting refresh')
        
        try {
          // Use TokenManager to refresh Google Drive token
          const newToken = await tokenManager.refreshGoogleDriveToken()
          if (newToken) {
            // Update the request with new token and retry
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
            return api(originalRequest)
          }
        } catch (refreshError) {
          console.log('❌ API: Google Drive token refresh error:', refreshError)
        }
      }
      
      // For non-cloud requests, try to refresh the user session using refresh token
      console.log('❌ API: User session expired, attempting session refresh')
      
      try {
        // Use TokenManager to refresh JWT token
        const newToken = await tokenManager.refreshJWTToken()
        if (newToken) {
          // Update the request with new token and retry
          originalRequest.headers['Authorization'] = `Basic ${newToken}`
          return api(originalRequest)
        }
      } catch (refreshError) {
        console.log('❌ API: Session refresh failed:', refreshError)
        
        // Clear auth data and redirect to login
        tokenManager.clearJWTTokens()
        
        // Redirect to login page
        window.location.href = '/login'
        
        return Promise.reject(new Error('User session expired and refresh failed'))
      }
    }
    
    return Promise.reject(error)
  }
)

// Authentication API methods
export const authAPI = {
  /**
   * Register a new user
   */
  register: async (userData) => {
    const response = await api.post('/api/auth/register', userData)
    return response.data
  },

  /**
   * Login user
   */
  login: async (credentials) => {
    const response = await api.post('/api/auth/login', credentials)
    return response.data
  },

  /**
   * Change password
   */
  changePassword: async (passwordData) => {
    const response = await api.put('/api/auth/change-password', passwordData)
    return response.data
  },

  /**
   * Forgot password
   */
  forgotPassword: async (email) => {
    const response = await api.post('/api/auth/forgot-password', { email })
    return response.data
  },

  /**
   * Health check
   */
  healthCheck: async () => {
    const response = await api.get('/api/auth/health')
    return response.data
  },

  /**
   * Get AI configuration for the current user
   */
  getAIConfiguration: async () => {
    const response = await api.get('/api/auth/ai-config')
    return response.data
  },

  /**
   * Update AI configuration for the current user
   */
  updateAIConfiguration: async (aiConfig) => {
    const response = await api.put('/api/auth/ai-config', aiConfig)
    return response.data
  }
}

// Notes API methods
export const notesAPI = {
  /**
   * Create a new note
   */
  createNote: async (noteData, password = null) => {
    const config = {}
    if (password) {
      config.headers = {
        'Sensitive-Password': password
      }
    }
    const response = await api.post('/api/notes', noteData, config)
    return response.data
  },

  /**
   * Get all notes with pagination
   * TEMPORARILY DISABLED - Returns mock data instead of calling backend
   */
  getNotes: async (params = {}) => {
    const { page = 0, size = 50, sortBy = 'lastModified', sortDir = 'desc', folderId } = params
    console.log('🚫 API: getNotes endpoint temporarily disabled - returning mock data')
    console.log('🔍 API: Would have called with params:', { page, size, sortBy, sortDir, folderId })
    
    // Return mock empty response to prevent frontend errors
    const mockResponse = {
      success: true,
      data: {
        content: [], // Empty array of notes
        totalElements: 0,
        totalPages: 0,
        size: size,
        number: page,
        first: true,
        last: true,
        numberOfElements: 0
      },
      message: "Notes endpoint temporarily disabled"
    }
    
    // Track successful sync to prevent retries
    localStorage.setItem('lastSuccessfulSync', Date.now().toString())
    
    return mockResponse
    
    // ORIGINAL CODE (commented out):
    // const requestParams = { page, size, sortBy, sortDir }
    // if (folderId !== undefined && folderId !== null) {
    //   requestParams.folderId = folderId
    // }
    // const hasCache = localStorage.getItem('lastSuccessfulSync')
    // const timeout = hasCache ? 30000 : 60000
    // const response = await api.get('/api/notes', {
    //   params: requestParams,
    //   timeout: timeout
    // })
    // localStorage.setItem('lastSuccessfulSync', Date.now().toString())
    // return response.data
  },

  /**
   * Get a specific note by ID
   */
  getNoteById: async (noteId) => {
    const response = await api.get(`/api/notes/${noteId}`)
    return response.data
  },

  /**
   * Update a note
   */
  updateNote: async (noteId, noteData) => {
    const response = await api.put(`/api/notes/${noteId}`, noteData)
    return response.data
  },

  /**
   * Delete a note
   */
  deleteNote: async (noteId) => {
    const response = await api.delete(`/api/notes/${noteId}`)
    return response.data
  },

  /**
   * Search notes
   */
  searchNotes: async (searchParams) => {
    const { term, searchInContent = false, page = 0, size = 50, folderId } = searchParams
    
    const requestParams = { term, searchInContent, page, size }
    if (folderId !== undefined && folderId !== null) {
      requestParams.folderId = folderId
    }
    
    const response = await api.get('/api/notes/search', {
      params: requestParams
    })
    return response.data
  },

  /**
   * Get user statistics
   */
  getUserStats: async () => {
    const response = await api.get('/api/notes/stats')
    return response.data
  },

  /**
   * Get list of deleted note IDs for cross-device sync
   */
  getDeletedNotes: async () => {
    const response = await api.get('/api/notes/deleted')
    return response.data
  },

  /**
   * Get list of updated note information for cross-device sync
   */
  getUpdatedNotes: async () => {
    const response = await api.get('/api/notes/updated')
    return response.data
  }
}

// Folders API methods
export const foldersAPI = {
  /**
   * Get all folders for the current user
   */
  getFolders: async () => {
    console.log('🔍 API: Getting folders')
    const response = await api.get('/api/folders')
    return response.data
  },

  /**
   * Get a specific folder by ID
   */
  getFolderById: async (folderId) => {
    console.log('🔍 API: Getting folder:', folderId)
    const response = await api.get(`/api/folders/${folderId}`)
    return response.data
  },

  /**
   * Create a new custom folder
   */
  createFolder: async (folderData) => {
    console.log('🔍 API: Creating folder:', folderData)
    const response = await api.post('/api/folders', folderData)
    return response.data
  },

  /**
   * Update a custom folder
   */
  updateFolder: async (folderId, updateData) => {
    console.log('🔍 API: Updating folder:', folderId, updateData)
    const response = await api.put(`/api/folders/${folderId}`, updateData)
    return response.data
  },

  /**
   * Delete a custom folder
   */
  deleteFolder: async (folderId) => {
    console.log('🔍 API: Deleting folder:', folderId)
    const response = await api.delete(`/api/folders/${folderId}`)
    return response.data
  },

  /**
   * Initialize default folders for a user
   */
  initializeDefaultFolders: async () => {
    console.log('🔍 API: Initializing default folders')
    const response = await api.post('/api/folders/initialize')
    return response.data
  },

  /**
   * Move a note to a different folder
   */
  moveNote: async (noteId, moveData) => {
    console.log('🔍 API: Moving note:', noteId, 'to:', moveData)
    const response = await api.post(`/api/folders/move/${noteId}`, moveData)
    return response.data
  },

  /**
   * Move note to root (no folder)
   */
  moveNoteToRoot: async (noteId) => {
    console.log('🔍 API: Moving note to root:', noteId)
    const response = await api.post(`/api/folders/move/${noteId}`, {})
    return response.data
  },

  /**
   * Quick action: Pin a note (move to Pinned folder)
   */
  pinNote: async (noteId) => {
    console.log('🔍 API: Pinning note:', noteId)
    const response = await api.post(`/api/folders/move/${noteId}`, { folderType: 'PINNED' })
    return response.data
  },

  /**
   * Quick action: Mark note as sensitive (move to Sensitive folder)
   */
  markNoteSensitive: async (noteId) => {
    console.log('🔍 API: Marking note as sensitive:', noteId)
    const response = await api.post(`/api/folders/move/${noteId}`, { folderType: 'SENSITIVE' })
    return response.data
  }
}

// Sensitive Folder API methods
export const sensitiveAPI = {
  /**
   * Authenticate for sensitive folder access
   */
  authenticateForAccess: async (credentials) => {
    console.log('🔐 API: Authenticating for sensitive folder access')
    const response = await api.post('/api/sensitive/authenticate', credentials)
    return response.data
  },

  /**
   * Get decrypted notes from sensitive folder
   */
  getSensitiveNotes: async (sessionToken, password) => {
    console.log('🔐 API: Getting sensitive notes')
    const response = await api.get('/api/sensitive/notes', {
      headers: {
        'Sensitive-Token': sessionToken
      },
      params: { password }
    })
    return response.data
  },

  /**
   * Save encrypted note to sensitive folder
   */
  saveSensitiveNote: async (noteData, sessionToken, password) => {
    console.log('🔐 API: Saving sensitive note')
    const response = await api.post('/api/sensitive/notes', noteData, {
      headers: {
        'Sensitive-Token': sessionToken
      },
      params: { password }
    })
    return response.data
  },

  /**
   * Revoke sensitive folder access
   */
  revokeAccess: async (sessionToken) => {
    console.log('🔐 API: Revoking sensitive access')
    const response = await api.post('/api/sensitive/revoke', {}, {
      headers: {
        'Sensitive-Token': sessionToken
      }
    })
    return response.data
  },

  /**
   * Check sensitive folder session status
   */
  getSessionStatus: async (sessionToken) => {
    console.log('🔐 API: Checking sensitive session status')
    const response = await api.get('/api/sensitive/status', {
      headers: {
        'Sensitive-Token': sessionToken
      }
    })
    return response.data
  },

  /**
   * Get session statistics (for monitoring)
   */
  getSessionStats: async () => {
    console.log('🔐 API: Getting session statistics')
    const response = await api.get('/api/sensitive/stats')
    return response.data
  }
}

// Utility functions
export const apiUtils = {
  /**
   * Set authentication token
   */
  setAuthToken: (token) => {
    if (token) {
      localStorage.setItem('authToken', token)
    } else {
      localStorage.removeItem('authToken')
    }
  },

  /**
   * Get authentication token
   */
  getAuthToken: () => {
    return localStorage.getItem('authToken')
  },

  /**
   * Clear authentication data and all cached user data
   * Enhanced security: Ensures complete data removal on logout
   * Now optimized for background execution with better error handling
   */
  clearAuth: async () => {
    console.log('🧹 Starting comprehensive logout data clearing...')
    
    try {
      // Clear localStorage auth and cloud data
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      localStorage.removeItem('cloudProvider')
      localStorage.removeItem('cloudAccessToken')
      localStorage.removeItem('cloudRefreshToken')
      localStorage.removeItem('cloudUserInfo')
      localStorage.removeItem('onboarding_selectedPlan')
      console.log('✅ localStorage cleared')
      
      // Clear session storage (but preserve local mode session data)
      const isLocalMode = localStorage.getItem('localModeEnabled') === 'true'
      if (!isLocalMode) {
        sessionStorage.removeItem('sensitiveToken')
        sessionStorage.removeItem('sensitiveExpiry')
        sessionStorage.removeItem('sensitivePassword')
        console.log('✅ sessionStorage cleared')
      } else {
        console.log('🔍 Skipping sessionStorage clearing - local mode enabled')
      }
      
      // Clear all IndexedDB databases with comprehensive error handling
      console.log('🗄️ Clearing IndexedDB databases...')
      
      // Use shorter timeout for background cleanup (3 seconds instead of 5)
      const results = await clearAllIndexedDBDataWithTimeout(3000)
      
      // Log detailed results
      results.forEach(result => {
        if (result.status === 'success') {
          console.log(`✅ IndexedDB cleared: ${result.database}`)
        } else if (result.status === 'skipped') {
          console.log(`⏭️ IndexedDB skipped: ${result.database} (${result.reason})`)
        } else if (result.status === 'error') {
          console.error(`❌ IndexedDB error: ${result.database} - ${result.error}`)
        }
      })
      
      // Check for any critical failures
      const errors = results.filter(r => r.status === 'error')
      if (errors.length > 0) {
        console.warn(`⚠️ Some IndexedDB databases couldn't be cleared: ${errors.map(e => e.database).join(', ')}`)
        // Don't throw error - partial clearing is better than none
      } else {
        console.log('✅ All applicable IndexedDB databases cleared successfully')
      }
      
    } catch (error) {
      console.error('❌ Critical error during IndexedDB clearing:', error)
      // Don't throw - we still want logout to complete
    }
    
    // Additional cleanup: Clear any remaining browser storage
    try {
      // Clear any cached service worker data if available
      if ('caches' in window) {
        const cacheNames = await Promise.race([
          caches.keys(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Cache keys timeout')), 1000))
        ])
        
        for (const cacheName of cacheNames) {
          if (cacheName.includes('notes') || cacheName.includes('cloud')) {
            try {
              await Promise.race([
                caches.delete(cacheName),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Cache delete timeout')), 500))
              ])
              console.log(`✅ Cache cleared: ${cacheName}`)
            } catch (cacheError) {
              console.warn(`⚠️ Failed to clear cache ${cacheName}:`, cacheError.message)
            }
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to clear service worker caches:', error.message)
    }
    
    console.log('🔐 Logout data clearing completed')
  },

  /**
   * Handle API errors with user-friendly messages
   */
  handleError: (error) => {
    if (error.response?.data?.message) {
      return error.response.data.message
    } else if (error.message) {
      return error.message
    }
    return 'An unexpected error occurred'
  },

  /**
   * Create Basic Auth token from email and password
   */
  createAuthToken: (email, password) => {
    return btoa(`${email}:${password}`)
  },

  /**
   * Verify IndexedDB has been properly cleared (for testing/debugging)
   */
  verifyIndexedDBClearing: verifyIndexedDBClearing,

  /**
   * Manually clear all IndexedDB data (for debugging)
   */
  clearAllIndexedDBData: clearAllIndexedDBData
}

// Export debugging functions globally in development mode
if (import.meta.env.DEV) {
  window.debugIndexedDB = {
    clearAll: clearAllIndexedDBData,
    verify: verifyIndexedDBClearing,
    checkDatabase: checkDatabaseExists
  }
  console.log('🔧 IndexedDB debugging functions available: window.debugIndexedDB')
}

export default api 